import React, { useEffect, useState } from 'react';
import { Button, Icon, Modal, Header, Grid, Dropdown, Segment, Label, } from 'semantic-ui-react';
import StyledDataGrid from '../components/StyledDataGrid';
import { useFormSubmissionsClient } from '../submissions/formSubmissions.client';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFluidFormConfigClient } from '../formBuilder/fluidFormConfig.client';
import { FluidForm } from '../../shared';
import { formatDate } from '../date.format';
import FieldDataRenderer from '../components/FieldDataRenderer';
import { DeleteButton } from '../components/DeleteButton';
import PageLayout from '../components/PageLayout';
import { renderStatusBadge } from '../components/StatusBadge';
import { PageLoader } from '../components/PageLoader';
import { Inbox } from 'lucide-react';
const SubmissionsPage = () => {
    const submissionsClient = useFormSubmissionsClient();
    const formConfigClient = useFluidFormConfigClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [submissions, setSubmissions] = useState([]);
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [selectedFormFilter, setSelectedFormFilter] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState();
    const navigate = useNavigate();
    const formParam = searchParams.get('form') ?? '';
    // Load submissions and forms
    useEffect(() => {
        fetchData(formParam);
    }, [formParam]);
    const fetchData = async (formKeyFilter) => {
        try {
            setLoading(true);
            setError(null);
            // Load forms for filter dropdown
            const [submissionsData, formsData] = await Promise.all([
                submissionsClient.listSubmissions(formKeyFilter),
                // Include soft-deleted forms so we can display their user-friendly names
                // for submissions that reference `deleted-...` formKeys.
                formConfigClient.listFormConfigs(true)
            ]);
            setSubmissions(submissionsData);
            setForms(formsData);
        }
        catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load submissions. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };
    const handleDeleteSubmission = async (submissionId) => {
        try {
            await submissionsClient.deleteSubmission(submissionId);
            // Refresh all data from server after successful deletion
            await fetchData();
        }
        catch (error) {
            console.error('Error deleting submission:', error);
            setError('Failed to delete submission. Please try again.');
        }
    };
    // Get field label from form config
    const getFieldLabel = (formKey, fieldKey) => {
        const form = forms.find(f => f.formKey === formKey);
        if (!form || !form.formConfig)
            return fieldKey;
        let formConfigService = new FluidForm(form.formConfig);
        let fieldConfig = formConfigService.getFieldConfigByFieldPath(fieldKey);
        return fieldConfig?.label || "-" + fieldKey;
    };
    const getFormName = (formConfigId) => {
        const form = forms.find(f => f.formKey === formConfigId);
        return form?.name || form?.formConfig.label || formConfigId;
    };
    // Group forms by formKey for efficient lookup
    const formsByKey = React.useMemo(() => {
        const formMap = new Map();
        forms.forEach(form => {
            formMap.set(form.formKey, form);
        });
        return formMap;
    }, [forms]);
    // Create dropdown options for form filter
    const formFilterOptions = [
        { key: 'all', value: '', text: 'All Forms' },
        ...forms.map(form => ({
            key: form.formKey,
            value: form.formKey,
            text: form.name
        }))
    ];
    // Handle form filter change
    const handleFormFilterChange = (value) => {
        // Update URL search params
        if (value) {
            setSearchParams({ form: value });
        }
        else {
            setSearchParams({});
        }
    };
    const { allFieldKeys, fieldLabels } = getFieldKeysFromSubmissions(submissions, getFieldLabel);
    // Create DataGrid data
    const createDataGridData = () => {
        if (submissions.length === 0)
            return { columns: [], rows: [] };
        const columns = createDataGridColumns(allFieldKeys, fieldLabels, formsByKey, getFormName, navigate, handleDeleteSubmission);
        const rows = createDataGridRows(submissions);
        return { columns, rows };
    };
    const { columns, rows } = createDataGridData();
    if (loading) {
        return React.createElement(PageLoader, { text: "Loading submissions..." });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(PageLayout, { title: "Submissions", subtitle: "View and manage form submissions", icon: React.createElement(Inbox, { className: "w-7 h-7 text-gray-700" }), error: error, filterSection: React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-4" },
                React.createElement("div", { className: "relative flex-grow max-w-md" },
                    React.createElement(Dropdown, { placeholder: "Filter by form", fluid: true, selection: true, options: formFilterOptions, value: formParam, onChange: (e, { value }) => handleFormFilterChange(value) }))) }, submissions.length === 0 ? (React.createElement("div", { className: "bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" },
            React.createElement("div", { className: "text-center py-20" },
                React.createElement(Icon, { name: "inbox", className: "mx-auto text-gray-300", size: "huge" }),
                React.createElement("h3", { className: "text-lg font-medium text-gray-900 mb-2 mt-4" }, "No submissions found"),
                React.createElement("p", { className: "text-gray-500" }, formParam ? 'Try adjusting your filters' : 'Submissions will appear here once forms are filled out')))) : (React.createElement("div", { className: "bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" },
            React.createElement("div", { style: { width: '100%' } },
                React.createElement(StyledDataGrid, { rows: rows, columns: columns, slotProps: { toolbar: { csvOptions: {
                                fields: ['submissionId', 'formKey', 'sentDate', 'userName', 'status',
                                    ...Array.from(allFieldKeys)],
                                escapeFormulas: false
                            } } }, showToolbar: true, initialState: {
                        pagination: {
                            paginationModel: { page: 0, pageSize: 25 },
                        },
                        pinnedColumns: { left: ['submissionId'], right: ['actions'] }
                    }, pageSizeOptions: [10, 25, 50, 100], checkboxSelection: false, disableRowSelectionOnClick: true }))))),
        React.createElement(Modal, { open: viewModalOpen, onClose: () => setViewModalOpen(false), size: "large" },
            React.createElement(Modal.Header, null,
                React.createElement(Icon, { name: "file alternate outline" }),
                "Submission Details"),
            React.createElement(Modal.Content, null, selectedSubmission && (React.createElement(Grid, null,
                React.createElement(Grid.Row, null,
                    React.createElement(Grid.Column, { width: 8 },
                        React.createElement(Header, { as: "h4" }, "Submission Information"),
                        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem' } },
                            React.createElement("div", null,
                                React.createElement("strong", null, "Submission ID:"),
                                React.createElement("br", null),
                                React.createElement("code", null, selectedSubmission.submissionId)),
                            React.createElement("div", null,
                                React.createElement("strong", null, "Form:"),
                                React.createElement("br", null),
                                formsByKey.get(selectedSubmission.formKey)?.deletedAt ? (React.createElement("span", null, getFormName(selectedSubmission.formKey))) : (React.createElement(Link, { to: `/form-config/${selectedSubmission.formKey}` }, getFormName(selectedSubmission.formKey)))),
                            React.createElement("div", null,
                                React.createElement("strong", null, "Submitted Date:"),
                                React.createElement("br", null),
                                formatDate(selectedSubmission.sentDate)),
                            selectedSubmission.userName && (React.createElement("div", null,
                                React.createElement("strong", null, "Slack User:"),
                                React.createElement("br", null),
                                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
                                    React.createElement(Icon, { name: "slack", size: "small", color: "purple" }),
                                    selectedSubmission.userName))))),
                    React.createElement(Grid.Column, { width: 8 },
                        React.createElement(Header, { as: "h4" }, "Submitted Data"),
                        React.createElement(Segment, null,
                            React.createElement("pre", { style: {
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    fontSize: '12px',
                                    whiteSpace: 'pre-wrap'
                                } }, JSON.stringify(selectedSubmission.data || {}, null, 2)))))))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: () => setViewModalOpen(false) }, "Close")))));
};
// Helper function to get field keys and labels from submissions
const getFieldKeysFromSubmissions = (submissions, getFieldLabel) => {
    if (submissions.length === 0) {
        return { allFieldKeys: new Set(), fieldLabels: new Map() };
    }
    // Get all unique field keys from all submissions with their form context
    const fieldKeysByForm = new Map();
    submissions.forEach(sub => {
        if (!fieldKeysByForm.has(sub.formKey)) {
            fieldKeysByForm.set(sub.formKey, new Set());
        }
        Object.keys(sub.data || {}).forEach(key => {
            fieldKeysByForm.get(sub.formKey).add(key);
        });
    });
    // Get unique form keys
    const uniqueFormKeys = Array.from(fieldKeysByForm.keys());
    // Find fields that are common across ALL forms
    const commonFieldKeys = new Set();
    if (uniqueFormKeys.length > 0) {
        // Start with fields from the first form
        const firstFormFields = fieldKeysByForm.get(uniqueFormKeys[0]) || new Set();
        firstFormFields.forEach(fieldKey => {
            // Check if this field exists in ALL other forms
            const existsInAllForms = uniqueFormKeys.every(formKey => fieldKeysByForm.get(formKey)?.has(fieldKey));
            if (existsInAllForms) {
                commonFieldKeys.add(fieldKey);
            }
        });
    }
    // Create a map of field keys to their best label
    const fieldLabels = new Map();
    commonFieldKeys.forEach(fieldKey => {
        // Use the first form to get the label
        const firstFormKey = uniqueFormKeys[0];
        if (firstFormKey) {
            fieldLabels.set(fieldKey, getFieldLabel(firstFormKey, fieldKey));
        }
    });
    return { allFieldKeys: commonFieldKeys, fieldLabels };
};
// Create DataGrid columns
const createDataGridColumns = (allFieldKeys, fieldLabels, formsByKey, getFormName, navigate, handleDeleteSubmission) => {
    return [
        {
            field: 'submissionId',
            headerName: 'Submission ID',
            width: 150,
            renderCell: (params) => (React.createElement(Link, { to: `/submissions/${params.value}`, className: "text-gray-600 text-sm font-mono hover:text-gray-900 hover:underline" }, params.value))
        },
        {
            field: 'formKey',
            headerName: 'Form',
            width: 150,
            renderCell: (params) => {
                const formKey = String(params.value || "");
                const isDeleted = Boolean(formsByKey.get(formKey)?.deletedAt);
                const label = getFormName(formKey);
                if (isDeleted) {
                    return React.createElement("span", { className: "text-gray-500 text-sm" }, label);
                }
                return (React.createElement(Link, { to: `/form-config/${formKey}`, className: "text-orange-600 font-medium text-sm hover:underline" }, label));
            }
        },
        {
            field: 'sentDate',
            headerName: 'Submitted Date',
            width: 180,
            type: 'date',
            valueFormatter: (value) => formatDate(value),
            renderCell: (params) => (React.createElement("span", { className: "text-gray-900 text-sm" }, formatDate(params.value)))
        },
        {
            field: 'userName',
            headerName: 'User Name',
            width: 150,
            renderCell: (params) => {
                if (params.value) {
                    const initial = String(params.value).charAt(0).toUpperCase();
                    return (React.createElement("div", { className: "flex items-center h-full" },
                        React.createElement("div", { className: "w-7 h-7 flex-shrink-0 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-xs font-medium mr-2" }, initial),
                        React.createElement("span", { className: "text-gray-900 text-sm" }, params.value)));
                }
                return '';
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const status = params.value || 'draft';
                return (React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: '100%' } }, renderStatusBadge(status)));
            }
        },
        {
            field: 'validationResults',
            headerName: 'Validation',
            width: 150,
            renderCell: (params) => {
                const validationResults = params.value?.fieldValidations;
                if (!validationResults || Object.keys(validationResults).length === 0) {
                    return null;
                }
                // Count unique failures and other errors
                const uniqueFailures = Object.values(validationResults).filter((result) => result.errorCode === 'unique_value_exists').length;
                const otherErrors = Object.values(validationResults).filter((result) => result.type !== 'correct').length;
                // Build display text
                let displayText = '';
                if (uniqueFailures > 0 && otherErrors > 0) {
                    displayText = `Unique Failure + ${otherErrors} Error${otherErrors > 1 ? 's' : ''}`;
                }
                else if (uniqueFailures > 0) {
                    displayText = 'Unique Fail';
                }
                else if (otherErrors > 0) {
                    displayText = `${otherErrors} Error${otherErrors > 1 ? 's' : ''}`;
                }
                if (displayText) {
                    return (React.createElement(Label, { color: "red", size: "small" },
                        React.createElement(Icon, { name: "exclamation triangle" }),
                        displayText));
                }
                return null;
            }
        },
        ...Array.from(allFieldKeys).map(fieldKey => ({
            field: fieldKey,
            headerName: fieldLabels.get(fieldKey) || fieldKey,
            width: 150,
            renderCell: (params) => {
                const formKey = params.row.formKey;
                const fieldValue = params.value;
                const formConfig = formsByKey.get(formKey)?.formConfig;
                // Now you have access to formKey, fieldValue, and formConfig for proper formatting
                return React.createElement(FieldDataRenderer, { value: fieldValue, formKey: formKey, fieldKey: fieldKey, formConfig: formConfig });
            }
        })),
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (React.createElement("div", { className: "flex items-center justify-end space-x-2" },
                React.createElement("button", { className: "p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors", onClick: () => navigate(`/submissions/${params.row.submissionId}`), title: "View" },
                    React.createElement(Icon, { name: "eye" })),
                React.createElement(DeleteButton, { iconOnly: true, onConfirm: () => handleDeleteSubmission(params.row.submissionId), title: "Delete submission", message: "Are you sure you want to delete this submission?" })))
        }
    ];
};
// Create DataGrid rows
const createDataGridRows = (submissions) => {
    return submissions.map(submission => ({
        id: submission.submissionId,
        submissionId: submission.submissionId,
        formKey: submission.formKey,
        sentDate: submission.sentDate,
        userName: submission.userName,
        status: submission.status,
        validationResults: submission.validationResults,
        ...(submission.data || {}),
        // Keep reference to full submission for actions
        ...submission
    }));
};
export default SubmissionsPage;
